/**
 * Global search hook
 *
 * Calls the unified search endpoint GET /api/v1/search instead of fanning out
 * across the customers/vehicles/work-orders list endpoints. The query is
 * disabled below the minimum length so short input makes no requests, and
 * previous data is kept while refetching to avoid the result list flickering.
 */
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

/** User-facing entity type. Mirrors @vehiclespace/shared GlobalSearchResult. */
export type GlobalSearchResultType = 'person' | 'company' | 'vehicle' | 'work_order';

export type SearchMatchType = 'exact' | 'partial';

/**
 * A single unified search result. Mirrors the backend contract
 * (@vehiclespace/shared `GlobalSearchResult`). The backend never returns an
 * `href`; the frontend derives the route from `type` + `id`.
 */
export interface GlobalSearchResult {
  type: GlobalSearchResultType;
  id: string;
  title: string;
  subtitle: string | null;
  context: string | null;
  matchType: SearchMatchType;
  matchedField?: string | null;
}

interface GlobalSearchResponse {
  results: GlobalSearchResult[];
}

/** Minimum query length before the search fires. */
export const MIN_SEARCH_LENGTH = 2;

export function useGlobalSearch(query: string, limit = 5) {
  const trimmed = query.trim();
  const enabled = trimmed.length >= MIN_SEARCH_LENGTH;

  return useQuery({
    queryKey: queryKeys.search.global(trimmed, limit),
    queryFn: () =>
      api.get<GlobalSearchResponse>('/search', {
        params: { q: trimmed, limit: String(limit) },
      }),
    enabled,
    placeholderData: keepPreviousData,
  });
}
